// Worker badge codes — the one place that knows what a badge scan looks like.
//
// Codes are minted server-side by generate_worker_badge_code() (mig 20260721053000):
//   BW1 + 12 chars of '23456789ABCDEFGHJKMNPQRSTVWXYZ'
// The alphabet drops 0/O, 1/I/L and U so a scuffed card, a photocopy, or a
// code read aloud over the radio can't land on a different worker. The regex
// here MUST stay in step with the CHECK constraint in the migration — the
// unit test pins the shared shape.

/** Prefix that marks a string as a worker badge (vs a pallet/SSCC scan). */
const BADGE_PREFIX = 'BW1'

/** Alphabet used by the minter — no 0/O, 1/I/L, U. */
export const BADGE_ALPHABET = '23456789ABCDEFGHJKMNPQRSTVWXYZ'

/** Canonical badge-code shape. Mirrors the mig-20260721053000 CHECK constraint. */
export const BADGE_CODE_RE = /^BW1[23456789ABCDEFGHJKMNPQRSTVWXYZ]{12}$/

/**
 * An RFID band's UID, as a reader emits it (mig 20261021010000).
 *
 * Unlike a badge code, this is NOT minted by us — it is the tag's own factory
 * identifier, so the shape is whatever the two common families produce: a
 * 125 kHz EM4100 read as decimal (10 digits), or a MIFARE 4- or 7-byte UID as
 * hex (8 or 14). All land inside 8–32 characters of [0-9A-F] once uppercased.
 *
 * The floor is 8 rather than 4 deliberately: four hex characters is SIXTEEN
 * BITS, and a band credits piece work on a tap. No real reader emits one that
 * short.
 *
 * DISJOINT FROM BADGE_CODE_RE by construction: a badge starts 'BW1' and 'W' is
 * not a hex digit, so no band UID can be read as a card code or the reverse.
 * That is what lets one wedge serve both without an ambiguous case.
 *
 * It is a LOOSE shape — an 18-digit SSCC pallet label matches it — and that is
 * fine, because shape is not the gate: a scan is only a credential if it
 * RESOLVES against the tenant's enrolled badges. Mirrors the CHECK in mig
 * 20261021010000, and the unit test pins the two together.
 */
export const RFID_UID_RE = /^[0-9A-F]{8,32}$/

/**
 * AIM symbology identifiers some scanners prepend when "transmit AIM ID" is
 * enabled: ']' + a code letter + a modifier digit. C = Code 128, Q = QR.
 * The UROVO ships with this OFF, but a mis-set device shouldn't silently fail
 * to scan — strip it rather than reject.
 */
const AIM_PREFIX_RE = /^\][A-Za-z][0-9]/

/**
 * Control characters a wedge can inject: the trailing CR/LF terminator, and
 * the GS (0x1D) separator some scanners emit mid-burst.
 */
const CONTROL_CHARS_RE = new RegExp('[\\x00-\\x1F\\x7F]', 'g')

/**
 * Normalise whatever the scanner/wedge delivered into a canonical badge code,
 * or null if it isn't one.
 *
 * Handles, in order: control characters, stray whitespace (some scanners pad),
 * an AIM identifier prefix, and lower-case (a hand-typed fallback when a card
 * is too damaged to scan).
 *
 * Returning null rather than throwing is deliberate: the capture screen feeds
 * this every keystroke-burst, and a non-badge scan (someone pointing the gun
 * at a pallet label) is an expected event, not an error.
 */
export function normaliseBadgeScan(raw: string): string | null {
  if (!raw) return null
  const s = raw
    .replace(CONTROL_CHARS_RE, '')
    .trim()
    .replace(AIM_PREFIX_RE, '')
    .trim()
    .toUpperCase()
  return BADGE_CODE_RE.test(s) ? s : null
}

/** True when `raw` is (or normalises to) a worker badge code. */
export function isBadgeScan(raw: string): boolean {
  return normaliseBadgeScan(raw) !== null
}

/**
 * Normalise whatever a band reader delivered into a canonical UID, or null if
 * it isn't one.
 *
 * Readers disagree about formatting the same tag: '04 A3 B2 C1', '04:a3:b2:c1'
 * and '04A3B2C1' are one band. EVERY non-alphanumeric character is dropped
 * (not just the ones we have seen), because a separator we have not met yet
 * would otherwise enrol the same physical band under a second code — and the
 * one-live-credential index cannot tell that those are the same thing.
 *
 * Deliberately stricter than that sounds: after stripping, the result must be
 * hex. A badge code survives the strip unchanged and is REFUSED here, so the
 * two normalisers can never both claim one scan.
 */
export function normaliseRfidScan(raw: string): string | null {
  if (!raw) return null
  const s = raw.replace(/[^0-9A-Za-z]/g, '').toUpperCase()
  return RFID_UID_RE.test(s) ? s : null
}

/** True when `raw` is (or normalises to) an RFID band UID. */
export function isRfidScan(raw: string): boolean {
  return normaliseRfidScan(raw) !== null
}

/**
 * Format a code for the human-readable line under the barcode: BW1-XXXX-XXXX-XXXX.
 * Grouping is for eyeball transcription only — the scannable payload and every
 * lookup use the ungrouped form, so never feed this back into normaliseBadgeScan.
 */
export function formatBadgeCodeForPrint(code: string): string {
  const body = code.slice(BADGE_PREFIX.length)
  return `${BADGE_PREFIX}-${body.slice(0, 4)}-${body.slice(4, 8)}-${body.slice(8, 12)}`
}

/**
 * Parse a code a HUMAN typed — the deliberate counterpart to
 * normaliseBadgeScan, which refuses the grouped form on purpose.
 *
 * Someone keying a badge reads the printed line (BW1-A2B3-C4D5-E6F7) and will
 * type the hyphens they can see, or spaces, or nothing. Separators are dropped
 * before the canonical shape test, so all three land on the same code.
 *
 * Kept OUT of normaliseBadgeScan deliberately: that one is fed every
 * keystroke-burst from a scanner, where accepting punctuation would widen what
 * a stray input can be mistaken for. Typing is an explicit, deliberate act at a
 * single field, so it can afford the looser parse.
 */
export function parseTypedBadgeCode(raw: string): string | null {
  return normaliseBadgeScan(raw.replace(/[\s-]/g, ''))
}
